import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';

import {
	Observable,
	filter,
	map,
	of,
	switchMap,
} from 'rxjs';

import { PopUpService } from 'ib/internetbank-core/ib-components/pop-ups/pop-up/pop-up.service';

import { ConstantsService } from '../../../common/services/constants.service';
import { LogData, UseractivityDataService } from '../../../common/services/useractivity.data.service';
import { CreditOrderSignDocumentsOnPopUpService } from '../../../pages/products/credits/credit-order-sign-documents-on-pop-up.service';
import { getProductOrderCreatePathForCardOrderItem, getProductOrderCreatePathForCreditOrderItem } from '../../../pages/shared/products/product-list-item/product-list-item-component-item.mapper';
import {
	CardOrderItem,
	CreditOrderItem,
	ProductOrderStatusCode,
} from '../../../products/left-panel/product-list/models/application-list.model';
import { ProductSuggestionItem } from '../../../products/left-panel/product-list/product-suggestion-list/models/product-suggestion.entities';
import { ProductSuggestionListFacade } from '../../../products/left-panel/product-list/product-suggestion-list/product-suggestion-list.facade';
import { CardOrderListFacade } from '../../../products/left-panel/product-list/request-product-order/card-order-list/card-order-list.facade';
import { CreditOrderListFacade } from '../../../products/left-panel/product-list/request-product-order/credit-order-list/credit-order-list.facade';
import { TransitionToGosuslugiComponent } from '../../../sections/operations/gosuslugi/transition-to-gosuslugi/transition-to-gosuslugi.component';
import { SuggestionLinkUtil } from '../../../sections/suggestions-section/utils/suggestion-link.utils';
import { RouteAction } from '../../notifications/models/route-action';
import { SplashBanner, SplashBannerTypeEnum } from '../models/splash-banner';

export interface NavigateParameters {
	routerLink?: Array<string>;
	queryParams?: Record<string, unknown>;

	absoluteRedirectLink?: string
}

@Component({
	selector: 'app-splash-banner',
	templateUrl: './splash-banner.component.html',
	styleUrls: ['./splash-banner.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SplashBannerComponent implements OnInit, OnChanges {

	@Input()
	public splashBanner: SplashBanner;

	@Output()
	public navigate = new EventEmitter<void>();

	public routeAction$: Observable<RouteAction | undefined>;

	constructor(
		private productSuggestionListFacade: ProductSuggestionListFacade,
		private creditOrderListFacade: CreditOrderListFacade,
		private cardOrderListFacade: CardOrderListFacade,
		private popUpService: PopUpService,
		private useractivityDataService: UseractivityDataService,
		private creditOrderSignDocumentsOnPopUpService: CreditOrderSignDocumentsOnPopUpService,
	) { }

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.splashBanner.currentValue) {
			this.routeAction$ = this.getRouteAction(this.splashBanner)
		}
	}

	public ngOnInit(): void {
	}

	public onNavigate() {
		this.navigate.emit();
	}

	private getRouteAction(splashBanner: SplashBanner): Observable<RouteAction | undefined> {

		let result: Observable<RouteAction | undefined> = of(undefined);

		switch (splashBanner.type) {
			case SplashBannerTypeEnum.CREDIT:

				result = this.creditOrderListFacade.getApplicationProducts()
					.pipe(
						switchMap(() => {
							return this.creditOrderListFacade.creditOrders$
								.pipe(
									filter((cardOrderItems: Array<CreditOrderItem>) => !!cardOrderItems.length),
									map((cardOrderItems: Array<CreditOrderItem>) => {
										const element = cardOrderItems.find((creditOrderItem: CreditOrderItem) => creditOrderItem.orderId === splashBanner.frontBlankId);
										const action = new RouteAction();

										switch (true) {
											case element?.status === ProductOrderStatusCode.WAITING_FOR_ESIA:
												action.onClickHandler = () => {
													const popUpComponentComponentRef = this.popUpService.open(
														TransitionToGosuslugiComponent,
														{ inputs: { closePopup: () => popUpComponentComponentRef.componentRef.instance.close() } },
													);
												}
												break;

											case element?.status === ProductOrderStatusCode.SIGN_APPLICATION:
												action.onClickHandler = () => {
													this.creditOrderSignDocumentsOnPopUpService.signContract(element.orderId, element.firstDocumentId!, element.secondDocumentId)
												}
												break;

											default:
												action.route = getProductOrderCreatePathForCreditOrderItem(element);

												break;
										}


										return action;
									}),
								);
						}))

				break;

			case SplashBannerTypeEnum.CREDIT_CARD:

				result = this.cardOrderListFacade.getApplicationProducts()
					.pipe(
						switchMap(() => {
							return this.cardOrderListFacade.cardOrders$
								.pipe(
									filter((cardOrderItems: Array<CardOrderItem>) => !!cardOrderItems.length),
									map((cardOrderItems: Array<CardOrderItem>) => {
										const element = cardOrderItems.find((cardOrderItem: CardOrderItem) => cardOrderItem.contractId === splashBanner.contractId);
										const action = new RouteAction();

										action.route = getProductOrderCreatePathForCardOrderItem(element);

										return action;
									}),
								);
						}),
					);

				break;

			case SplashBannerTypeEnum.PREAPPROVAL:

				result = this.productSuggestionListFacade.getSuggestionAllList()
					.pipe(
						switchMap(() => {
							return this.productSuggestionListFacade.suggestionAllList$
								.pipe(
									filter((productSuggestions: Array<ProductSuggestionItem>) => !!productSuggestions.length),
									map((productSuggestions: Array<ProductSuggestionItem>) => {
										const element: ProductSuggestionItem | undefined = productSuggestions.find((productSuggestionItem: ProductSuggestionItem) => productSuggestionItem.frontId === splashBanner.frontId);
										const action = element
											? SuggestionLinkUtil.getSuggestionRouteAction(element)
											: undefined;

										return action;
									}),
								);
						}),
					);

				break;

			case SplashBannerTypeEnum.COMMON: {
				const action = new RouteAction();

				if (splashBanner.deepLink.includes(ConstantsService.IB_PROD_URL)) {
					const routerLink: string = splashBanner.deepLink.replace(`https://${ConstantsService.IB_PROD_URL}`, '');

					action.route = [routerLink];
				} else {
					action.onClickHandler = () => {
						window.open(splashBanner.deepLink, '_blank')?.focus();
					};
				}

				result = of(action);
				break;
			}

			default:
				break;
		}

		return result.pipe(
			map((action: RouteAction | undefined) => {
				if (action) {
					const initialClickHandler = action.onClickHandler;
					action.onClickHandler = () => {

						this.useractivityDataService.log(
							new LogData(
								'BANNER',
								'SPLASH',
								'CLICK_BUTTON',
								[
									{
										param: 'id',
										value: splashBanner.id,
									},
								],
							),
						)
							.subscribe();

						if (initialClickHandler) {
							initialClickHandler(null);
						}

						this.onNavigate();
					}
				}

				return action;
			}),
		);
	}

}
